/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';
import type { NavContextDocument, NavContextKey, NavDocument } from '@/lib/adapters/nav-content';

/**
 * Shared state between the header's gender tabs and the nav surfaces (desktop
 * silo row + mobile menu): which NavDocument is being served and which gender
 * context is active. Client state only — live NM swaps the silo tree per
 * WOMEN/MEN tab without navigation, defaulting to WOMEN
 * (.reference/harvest/live-nm-mobile-drawer-structure.json "gender tabs").
 */
export interface NavContentContextValue {
    document: NavDocument;
    /**
     * The selected context key. May name a context the document does not carry
     * (single-context BG documents keep the header tabs cosmetic) — consumers
     * render {@link activeContext}, which falls back to the default context.
     */
    contextKey: NavContextKey;
    setContextKey: (key: NavContextKey) => void;
    /** The context to render: `contexts[contextKey]`, else the default context. */
    activeContext: NavContextDocument;
}

const NavContentContext = createContext<NavContentContextValue | null>(null);

const EMPTY_CONTEXT: NavContextDocument = { silos: [] };

/** Provides the nav document + gender-context state to header nav surfaces. */
export function NavContentProvider({ document, children }: { document: NavDocument; children: ReactNode }) {
    const [contextKey, setContextKey] = useState<NavContextKey>(document.defaultContext);

    const value = useMemo<NavContentContextValue>(() => {
        const activeContext =
            document.contexts[contextKey] ?? document.contexts[document.defaultContext] ?? EMPTY_CONTEXT;
        return { document, contextKey, setContextKey, activeContext };
    }, [document, contextKey]);

    return <NavContentContext value={value}>{children}</NavContentContext>;
}

/**
 * Read the nav-content context. Returns `null` outside a provider so hosts
 * with no curated nav (isolated stories, tests, the checkout header) keep
 * working — callers fall back to local state.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useNavContext(): NavContentContextValue | null {
    return useContext(NavContentContext);
}
